import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/api";
import { useApi, post, put, Panel, PageHead, Stat, QuickForm, DataTable, StatusBadge } from "@/crm/ui";

const ROLES = ["admin", "manager", "operations", "finance", "employee", "partner", "user"];

const MultiSelect = ({ label, options, value, onChange, testid }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    <select multiple value={value} data-testid={testid}
            onChange={(e) => onChange(Array.from(e.target.selectedOptions).map((o) => o.value))}
            className="h-24 w-full rounded-md border border-input bg-white px-2 py-1 text-xs">
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

export default function Users() {
  const users = useApi("/crm/users");
  const options = useApi("/crm/options");
  const [form, setForm] = useState({ role: "employee" });
  const [access, setAccess] = useState({ bank_access: [], location_access: [], product_access: [] });
  const [edit, setEdit] = useState(null);
  const [temp, setTemp] = useState(null);
  const rows = users.data || [];
  const opt = (list, id, name) => (list || []).map((r) => ({ value: r[id], label: r[name] }));

  const create = async () => {
    const r = await post("/crm/users", { ...form, ...access }, "User created");
    if (r) {
      setForm({ role: "employee" });
      setAccess({ bank_access: [], location_access: [], product_access: [] });
      if (r.temporary_password) setTemp({ email: r.email, password: r.temporary_password });
      users.reload();
    }
  };

  const save = async () => {
    const r = await put(`/crm/users/${edit.email}`, {
      role: edit.role, status: edit.status, name: edit.name, mobile: edit.mobile,
      department: edit.department, team: edit.team, branch: edit.branch,
      employee_id: edit.employee_id, partner_id: edit.partner_id,
      bank_access: edit.bank_access || [], location_access: edit.location_access || [],
      product_access: edit.product_access || [],
    }, "User updated");
    if (r) { setEdit(null); users.reload(); }
  };

  const resetPassword = async (row) => {
    const password = `Gr${Math.random().toString(36).slice(2, 10)}!1`;
    const r = await put(`/crm/users/${row.email}`, { password }, "Temporary password set");
    if (r) setTemp({ email: row.email, password });
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <PageHead eyebrow="Administration" title="User management"
                subtitle="Create portal users, link them to an employee or partner record, set their role and restrict the banks, locations and products they can see. Enforcement happens server-side." />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Total users" value={rows.length} testid="user-total" />
        <Stat label="Active" value={rows.filter((u) => u.status !== "Inactive").length} testid="user-active" />
        <Stat label="Linked to employees" value={rows.filter((u) => u.employee).length} testid="user-employees" />
        <Stat label="Partner portals" value={rows.filter((u) => u.partner).length} testid="user-partners" />
      </div>

      <Panel title="Create user" testid="create-user-panel"
             aside={<span className="text-xs text-muted-foreground">
               No email provider is configured — the temporary password is shown to you once
             </span>}>
        <QuickForm testid="user-form" values={form} submitLabel="Create user" onSubmit={create}
                   onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))}
                   fields={[{ key: "email", label: "Email" }, { key: "name", label: "Full Name" },
                     { key: "mobile", label: "Mobile" },
                     { key: "role", label: "Role", options: ROLES },
                     { key: "department", label: "Department" }, { key: "team", label: "Team" },
                     { key: "branch", label: "Branch" },
                     { key: "employee_id", label: "Link Employee", options: opt(options.data?.employees, "employee_id", "employee_name") },
                     { key: "partner_id", label: "Link Partner", options: opt(options.data?.partners, "partner_id", "partner_name") }]} />
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <MultiSelect label="Bank access (blank = all)" testid="bank-access-select"
                       options={opt(options.data?.banks, "bank_id", "bank_name")}
                       value={access.bank_access}
                       onChange={(v) => setAccess((a) => ({ ...a, bank_access: v }))} />
          <MultiSelect label="Location access" testid="location-access-select"
                       options={opt(options.data?.locations, "location_id", "location_name")}
                       value={access.location_access}
                       onChange={(v) => setAccess((a) => ({ ...a, location_access: v }))} />
          <MultiSelect label="Product access" testid="product-access-select"
                       options={opt(options.data?.products, "product_id", "product_name")}
                       value={access.product_access}
                       onChange={(v) => setAccess((a) => ({ ...a, product_access: v }))} />
        </div>
      </Panel>

      <Panel title={`Users (${rows.length})`} testid="users-panel">
        <DataTable testid="users-table" rows={rows}
                   columns={[{ key: "email", label: "Email" }, { key: "name", label: "Name" },
                     { key: "role", label: "Role", render: (r) => <StatusBadge value={r.role} /> },
                     { key: "mobile", label: "Mobile" },
                     { key: "employee", label: "Employee", render: (r) => r.employee?.employee_name || "—" },
                     { key: "partner", label: "Partner", render: (r) => r.partner?.partner_name || "—" },
                     { key: "bank_access", label: "Bank Access", render: (r) => (r.bank_access || []).join(", ") || "All" },
                     { key: "location_access", label: "Location Access", render: (r) => (r.location_access || []).join(", ") || "All" },
                     { key: "created_at", label: "Created", render: (r) => fmtDate(r.created_at) },
                     { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                     { key: "actions", label: "Actions", render: (r) => (
                       <span className="flex gap-1">
                         <Button size="sm" variant="outline" className="bg-white text-[10px]"
                                 data-testid={`edit-user-${r.email}`}
                                 onClick={() => setEdit({ ...r })}>Edit</Button>
                         <Button size="sm" variant="outline" className="bg-white text-[10px]"
                                 data-testid={`reset-password-${r.email}`}
                                 onClick={() => resetPassword(r)}>Reset password</Button>
                       </span>) }]} />
      </Panel>

      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent className="max-w-2xl bg-white" data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle>Edit user — {edit?.email}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid gap-3 sm:grid-cols-2">
              {[["name", "Name"], ["mobile", "Mobile"], ["department", "Department"],
                ["team", "Team"], ["branch", "Branch"]].map(([k, l]) => (
                <div key={k} className="space-y-1">
                  <Label className="text-xs">{l}</Label>
                  <Input value={edit[k] || ""} data-testid={`edit-user-${k}`}
                         onChange={(e) => setEdit((u) => ({ ...u, [k]: e.target.value }))} />
                </div>))}
              <div className="space-y-1">
                <Label className="text-xs">Role</Label>
                <select className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm"
                        data-testid="edit-user-role" value={edit.role}
                        onChange={(e) => setEdit((u) => ({ ...u, role: e.target.value }))}>
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <select className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm"
                        data-testid="edit-user-status" value={edit.status || "Active"}
                        onChange={(e) => setEdit((u) => ({ ...u, status: e.target.value }))}>
                  {["Active", "Inactive"].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <MultiSelect label="Bank access" testid="edit-bank-access"
                           options={opt(options.data?.banks, "bank_id", "bank_name")}
                           value={edit.bank_access || []}
                           onChange={(v) => setEdit((u) => ({ ...u, bank_access: v }))} />
              <MultiSelect label="Location access" testid="edit-location-access"
                           options={opt(options.data?.locations, "location_id", "location_name")}
                           value={edit.location_access || []}
                           onChange={(v) => setEdit((u) => ({ ...u, location_access: v }))} />
            </div>)}
          <DialogFooter>
            <Button variant="outline" className="bg-white" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={save} data-testid="save-user-btn">Save user</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!temp} onOpenChange={() => setTemp(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="temp-password-dialog">
          <DialogHeader><DialogTitle>Temporary password</DialogTitle></DialogHeader>
          <p className="text-sm">Share this with <b>{temp?.email}</b> securely. It is shown only once
            and the user must change it after signing in.</p>
          <p className="num rounded-md border border-border bg-muted px-3 py-2 text-sm font-bold"
             data-testid="temp-password-value">{temp?.password}</p>
          <DialogFooter>
            <Button onClick={() => { navigator.clipboard?.writeText(temp.password); toast.success("Copied"); }}
                    data-testid="copy-temp-password-btn">Copy</Button>
            <Button variant="outline" className="bg-white" onClick={() => setTemp(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
